import { PageLayout } from '@repo/ui/components/page-layout'
import { getErrorCode } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FolderLock } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { NodeDetails } from '@/modules/file-manager/components/details-node'
import { DetailsPanel } from '@/modules/file-manager/components/details-panel'
import { EmptyFolderActions } from '@/modules/file-manager/components/empty-folder-actions'
import { EmptyFolderState } from '@/modules/file-manager/components/empty-folder-state'
import { FilePreview } from '@/modules/file-manager/components/file-preview'
import { FolderBar } from '@/modules/file-manager/components/folder-bar'
import { FolderBreadcrumb } from '@/modules/file-manager/components/folder-breadcrumb'
import { FolderOrigin } from '@/modules/file-manager/components/folder-origin'
import { FOLDER_COLUMNS } from '@/modules/file-manager/components/node-columns'
import { NodeGrid } from '@/modules/file-manager/components/node-grid'
import { UnreachableState } from '@/modules/file-manager/components/unreachable-state'
import { useFoldersFirst } from '@/modules/file-manager/hooks/use-folders-first'
import { ListingProvider } from '@/modules/file-manager/hooks/use-listing'
import {
	listingSearchMiddlewares,
	validateListingSearch
} from '@/modules/file-manager/lib/folder-search'
import { folderSectionOf } from '@/modules/file-manager/lib/folder-section'
import { folderAncestorsQueryOptions } from '@/modules/file-manager/query/query-options'
import { useFolderChildren } from '@/modules/file-manager/query/use-folder-children'
import { GlobalSearch } from '@/modules/search/components/global-search'

export const Route = createFileRoute('/(private)/(shell)/files/$nodeId/')({
	component: FolderPage,
	// Canonical on the way out, forgiving on the way in: OPSSTD-6606, and
	// `folder-search.ts` holds the whole of why they are two halves.
	search: { middlewares: listingSearchMiddlewares },
	validateSearch: validateListingSearch
})

/**
 * Every folder, whoever it belongs to.
 *
 * **One address per folder, and the section is the data's.** A folder in the
 * caller's own tree and one reached through a share are the same page at the
 * same path, so a URL copied from the browser bar opens the same folder for
 * anybody who can see it. Which section it belongs to — «I miei file» or
 * «Condivisi con me» — is read off `list-ancestors`' `meta.origin` by
 * `folder-section.ts`, never off the path, and until that answer arrives the
 * page says neither.
 *
 * **The origin badge falls silent in the caller's own tree by itself.** It
 * names the grant that opened the top of the path, and `meta.origin` is null
 * where there is none, so the page draws it unconditionally and lets the data
 * decide.
 *
 * The order lives in the URL because it is shareable and must survive a
 * reload. The literals here are the client's half of a contract the SDK owns:
 * they are handed to `folderChildrenQueryOptions`, whose parameter is typed
 * from the generated request, so a column the server stops offering fails to
 * compile rather than failing at runtime on the second page.
 *
 * The page is two rows and a listing: the folder's name, the row of controls,
 * and the grid as the only thing that scrolls. The order control and the view
 * switch belong to the second row rather than to the grid, so they are still
 * there when the folder is empty, the read failed or the connection is gone.
 * `PageLayout` owns the rows and the rhythm between them, so nothing about
 * heights or insets is decided here.
 *
 * **No `staticData.breadcrumb`, deliberately.** The shell's breadcrumb draws
 * one crumb per matched route, and a folder's path is as deep as the tree is;
 * it also has to link each step to its own folder, which a per-match crumb
 * cannot express. So the path is drawn here, from the chain the server
 * returns, in the row that owns it.
 */
function FolderPage() {
	const { nodeId } = Route.useParams()
	const search = Route.useSearch()
	// Not in the search: it is a stored preference rather than part of the
	// address, for the reason `use-folders-first.ts` gives. It still reaches the
	// request and still keys the cache.
	const foldersFirst = useFoldersFirst()
	const { t } = useTranslation()
	const path = useQuery(folderAncestorsQueryOptions(nodeId))
	const section = path.data
		? folderSectionOf(path.data.meta.origin)
		: undefined
	const children = useFolderChildren(
		nodeId,
		{
			by: search.by,
			direction: search.direction,
			foldersFirst
		},
		{ categories: search.categories, people: search.people }
	)

	// Either read can be the first to hear a refusal: they are two cache
	// entries with two ages, and a revocation invalidates the children alone.
	const unreachable = [path.error, children.query.error].some(
		(error) => getErrorCode(error) === 'not_found'
	)

	if (unreachable) {
		return (
			<UnreachableState
				description={t('client:files.unreachable.folder.description')}
				icon={<FolderLock />}
				title={t('client:files.unreachable.folder.title')}
			/>
		)
	}

	return (
		/*
		 * Keyed on the folder so the selection empties by remounting rather
		 * than by an effect watching the identifier. Leaving one folder with
		 * things picked and arriving in another with them still picked is a
		 * selection that no longer refers to anything on screen.
		 */
		<ListingProvider
			key={nodeId}
			nodes={children.nodes}
			parentId={nodeId}
			parentRole={children.parentRole}
		>
			<PageLayout
				aside={
					<DetailsPanel>
						<NodeDetails />
					</DetailsPanel>
				}
				bar={<FolderBar />}
				breadcrumb={
					<>
						<div className='min-w-0 flex-1'>
							<FolderBreadcrumb
								ancestors={path.data?.data}
								isPending={path.isPending}
								nodeId={nodeId}
								section={section}
							/>
						</div>
						<FolderOrigin origin={path.data?.meta.origin} />
					</>
				}
				search={<GlobalSearch />}
			>
				<NodeGrid
					columns={FOLDER_COLUMNS}
					// The invitation to fill a folder is for the caller's own
					// tree; a shared one keeps the grid's plain empty state.
					empty={
						section === 'personal' ? (
							<EmptyFolderState action={<EmptyFolderActions />} />
						) : undefined
					}
					nodes={children.nodes}
					query={children.query}
				/>
			</PageLayout>

			<FilePreview />
		</ListingProvider>
	)
}
